import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Microsoft360ViewContext } from '../../../shared/contexts/microsoft-360-view.context';
import { combineLatest, forkJoin, Observable, of, Subscription } from 'rxjs';
import {
  AccountType,
  LicensingState,
  Microsoft360View,
  MicrosoftDialPlanFieldConfig,
  SharedCallingRoutingPolicy,
  State,
  StatusCategory,
  TeamsPhoneNumberAssignment,
  TeamsVoicePolicies,
} from '../../../shared/models/generated/ziroModels';
import { BottomNavButton } from '../../../shared/bottom-nav/bottom-nav.component';
import { ButtonStyles } from '../../../button/button.component';
import {
  BottomNavClearButtonsList,
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ZiroMicrosoftTeamsCallingDialPlanSelectorFormComponent } from './teams-calling/microsoft-teams-calling-dial-plan-selector-form/microsoft-teams-calling-dial-plan-selector-form.component';
import { UnansweredCallForwardingSettingsComponent } from './delegate-management/unanswered-call-forwarding-settings.component';
import { delayWhen, finalize, map, switchMap, tap } from 'rxjs/operators';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { ToastService } from '../../../shared/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { MicrosoftDialPlansResource } from '../../shared/resources/ms-dial-plans.resource';
import { SystemStatusContext } from '../../../shared/contexts/system-status.context';
import { SharedCallingPoliciesContext } from '../../shared/contexts/shared-calling-policies.context';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { TelephoneNumberFilter } from '../../../shared/filters/telephone-number.filter';

export enum TeamsCallingContainerTabs {
  CALLING,
  UNANSWERED_CALL_FORWARDING,
}

@Component({
  selector: 'ziro-teams-calling-container',
  templateUrl: './teams-calling-container.component.html',
  providers: [SharedCallingPoliciesContext],
  standalone: false,
})
export class TeamsCallingContainerComponent implements OnInit, OnDestroy {
  private _router = inject(Router);
  private _microsoft360ViewContext = inject(Microsoft360ViewContext);
  private _route = inject(ActivatedRoute);
  private _bottomNavService = inject(BottomNavService);
  private _toastService = inject(ToastService);
  private _translateService = inject(TranslateService);
  private _microsoftDialPlansResource = inject(MicrosoftDialPlansResource);
  private _systemStatusContext = inject(SystemStatusContext);
  private _sharedCallingPoliciesContext = inject(SharedCallingPoliciesContext);
  private _ziroModalService = inject(ZiroModalService);
  private _telephoneNumberFilter = inject(TelephoneNumberFilter);
  private _subscriptions = new Subscription();

  private _allMicrosoftDialPlanGroups: MicrosoftDialPlanFieldConfig[];

  tabs = TeamsCallingContainerTabs;
  activeTab: TeamsCallingContainerTabs = TeamsCallingContainerTabs.CALLING;
  isDelegateManagementEnabled = false;
  delegateManagementDisabledTooltip = '';
  hasCallingTabError = false;
  hasForwardingTabError = false;
  ziroIcons = ZiroIcons;
  isLoading = true;
  upn = '';
  teamsLine: TeamsPhoneNumberAssignment;
  voicePolicies: TeamsVoicePolicies;
  microsoftDialPlanGroups: MicrosoftDialPlanFieldConfig[];
  isZpcSyncWarningPresent = false;
  ms360View: Microsoft360View;
  isResourceAccount: boolean;
  sharedCallingPolicies: SharedCallingRoutingPolicy[];
  tenantId: string;
  selectedDialPlanGroupId: number;

  @ViewChild(ZiroMicrosoftTeamsCallingDialPlanSelectorFormComponent)
  dialPlanSelectorFormComponent: ZiroMicrosoftTeamsCallingDialPlanSelectorFormComponent;
  @ViewChild(UnansweredCallForwardingSettingsComponent)
  unansweredCallForwardingSettingsComponent: UnansweredCallForwardingSettingsComponent;

  ngOnInit(): void {
    this.upn = this._route.snapshot.paramMap.get('upn');
    this.tenantId = this._route.snapshot.params.tenantId;

    if (this._route.snapshot.queryParamMap.get('unansweredCallForwarding')) {
      this.activeTab = TeamsCallingContainerTabs.UNANSWERED_CALL_FORWARDING;
    }
    this._initDataLoading();
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    this._bottomNavService.dispatch(new BottomNavClearButtonsList());
  }

  onForwardingTabSelected(): void {
    this.selectedDialPlanGroupId = this.dialPlanSelectorFormComponent?.selectedDialPlanGroup?.id;
  }

  private _initDataLoading(): void {
    const ms360ViewSub = this._microsoft360ViewContext.state$.subscribe((ms360View: Microsoft360View) => {
      this.isDelegateManagementEnabled = ms360View.accountType !== AccountType.RESOURCE_ACCOUNT;
      if (ms360View.accountType === AccountType.RESOURCE_ACCOUNT) {
        this.delegateManagementDisabledTooltip =
          'tkey;microsoft_360.view.teams.call_forward_settings.tab.disabled_for_resource_account.tooltip';
      }
    });
    this._subscriptions.add(ms360ViewSub);

    const sharedCallingPolicies$ = this._sharedCallingPoliciesContext.state$.pipe(
      map((policies) => {
        this.sharedCallingPolicies = policies;
      }),
    );

    const ms360View$ = this._microsoft360ViewContext.state$.pipe(
      switchMap((ms360View: Microsoft360View) => {
        this.ms360View = ms360View;
        this.isResourceAccount = ms360View.accountType === AccountType.RESOURCE_ACCOUNT;
        if (ms360View.microsoft365UserLicenses) {
          if (
            ms360View.licensingStatus.teamsPhoneStatus === LicensingState.LICENSED ||
            ms360View.licensingStatus.teamsPhoneStatus === LicensingState.LICENSED_WITH_MICROSOFT_CALLING_PLAN
          ) {
            return this._allMicrosoftDialPlanGroups
              ? of(null)
              : this._microsoftDialPlansResource.getAllDialPlanGroups(this.tenantId);
          } else {
            this._router.navigate([`/helpdesk/tenants/${this.tenantId}/360-view/${this.upn}`]);
          }
        } else {
          this._router.navigate(['/']);
        }
      }),
      map((dialPlanGroups: MicrosoftDialPlanFieldConfig[]) => {
        if (dialPlanGroups) {
          this.teamsLine = this.ms360View.teamsPhoneNumberAssignment
            ? { ...this.ms360View.teamsPhoneNumberAssignment }
            : null;
          this.voicePolicies = this.ms360View.teamsVoicePolicies ? { ...this.ms360View.teamsVoicePolicies } : null;
          this._allMicrosoftDialPlanGroups = dialPlanGroups;
        }
      }),
    );

    const contextSub = combineLatest([ms360View$, sharedCallingPolicies$]).subscribe(() => {
      this.microsoftDialPlanGroups = this._allMicrosoftDialPlanGroups.filter(
        (dpg) => String(dpg.tenantId) === this.tenantId,
      );
      this.isLoading = false;
      setTimeout(() => this._initBottomNav(), 0);
    });
    this._subscriptions.add(contextSub);

    const systemStatusSub = this._systemStatusContext.state$.subscribe((systemStatus) => {
      const syncStatus = systemStatus.healthStatuses.find((status) => status.category === StatusCategory.PROXY_SERVER);
      this.isZpcSyncWarningPresent = syncStatus && syncStatus.state !== State.OK;
    });
    this._subscriptions.add(systemStatusSub);
  }

  private _initBottomNav(): void {
    const buttonsList: BottomNavButton[] = [
      {
        id: 'teams-calling-cancel',
        dataAutomation: 'teams-calling-cancel',
        label: 'tkey;global.button.cancel.text',
        buttonClass: ButtonStyles.DEFAULT,
        cb: () => {
          this._router.navigate(['../'], { relativeTo: this._route });
        },
      },
    ];

    const existingNumber =
      this.teamsLine?.lineUriExtension ||
      this.teamsLine?.lineUri ||
      this.dialPlanSelectorFormComponent.dialPlanGroupSharedCallingPolicy?.resourceAccountLineUri;
    if (existingNumber) {
      buttonsList.push({
        id: 'teams-calling-delete',
        dataAutomation: 'teams-calling-delete',
        label: 'tkey;global.button.delete.text',
        buttonClass: ButtonStyles.DANGER,
        icon: this.ziroIcons.DELETE_SMALL,
        cb: () => this._onDeleteClicked(existingNumber),
      });
    }

    buttonsList.push({
      id: 'teams-calling-save',
      dataAutomation: 'teams-calling-save',
      label: 'tkey;global.button.save.text',
      buttonClass: ButtonStyles.PRIMARY,
      icon: ZiroIcons.SAVE_SUCCESS,
      cb: this._onSave,
    });
    this._bottomNavService.dispatch(new BottomNavUpdateButtonsList(buttonsList));
  }

  private _onSave = (): void => {
    this._bottomNavService.setButtonPendingState('teams-calling-save', true);
    this._bottomNavService.setButtonDisabledState('teams-calling-delete', true);
    this._bottomNavService.setButtonDisabledState('teams-calling-cancel', true);

    this.dialPlanSelectorFormComponent.validateForm();
    if (this.isDelegateManagementEnabled) {
      this.unansweredCallForwardingSettingsComponent.validateForm();
    }

    const callingFormValid = this.dialPlanSelectorFormComponent.isCallingFormValid();
    const forwardingFormValid =
      !this.isDelegateManagementEnabled || this.unansweredCallForwardingSettingsComponent.isFormValid();

    this.hasCallingTabError = !callingFormValid;
    this.hasForwardingTabError = !forwardingFormValid;

    if (!callingFormValid || !forwardingFormValid) {
      this._bottomNavService.dispatch(new BottomNavUpdateState({ hasValidationError: true }));
      this._bottomNavService.setButtonPendingState('teams-calling-save', false);
      this._bottomNavService.setButtonDisabledState('teams-calling-delete', false);
      this._bottomNavService.setButtonDisabledState('teams-calling-cancel', false);
      return;
    }

    const sub = this.dialPlanSelectorFormComponent
      .save()
      .pipe(
        switchMap(() =>
          this.isDelegateManagementEnabled ? this.unansweredCallForwardingSettingsComponent.save() : of(null),
        ),
        switchMap(() => this._microsoft360ViewContext.refresh(this.upn)),
        tap(() => this._successToast()),
        finalize(() => {
          this._bottomNavService.setButtonPendingState('teams-calling-save', false);
          this._bottomNavService.setButtonDisabledState('teams-calling-delete', false);
          this._bottomNavService.setButtonDisabledState('teams-calling-cancel', false);
        }),
      )
      .subscribe();

    this._subscriptions.add(sub);
  };

  private _successToast(): void {
    this._toastService.push(
      ToastTypes.SUCCESS,
      ZiroIcons.TEAMS_CALLING,
      'tkey;shared.toast.save.success.title',
      this._translateService.instant('tkey;shared.toast.save.success.message', {
        type: this._translateService.instant('tkey;shared.model.microsoft_teams_calling.text'),
        name: this.upn,
      }),
    );

    this._router.navigate(['../'], { relativeTo: this._route });
  }

  private _onDeleteClicked(existingNumber: string): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_BIG,
        iconClass: encodeURIComponent('text-danger'),
        title: this._translateService.instant('tkey;microsoft_360.view.teams.calling.delete.modal.title'),
        promptBody: this._translateService.instant('tkey;microsoft_360.view.teams.calling.delete.modal.text', {
          number: this._telephoneNumberFilter.transform(existingNumber),
          userPrincipalName: this.upn,
        }),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => this._onConfirmDelete(),
          },
        ],
      },
    };

    this._ziroModalService
      .openPromptModal(() => options.modalViewProperties, options)
      .subscribe((isDeleted) => {
        if (isDeleted) {
          this._toastService.push(
            ToastTypes.INFO,
            `${this.ziroIcons.DELETE_SMALL} text-danger`,
            'tkey;shared.toast.delete.success.title',
            `${this._translateService.instant('tkey;microsoft_360.view.teams.calling.toast.text')} - <strong>${this.upn} | ${existingNumber}
            </strong>`,
          );
          this._router.navigate(['../'], { relativeTo: this._route });
        }
      });
  }

  private _onConfirmDelete(): Observable<boolean> {
    const deleteReqs = [this.dialPlanSelectorFormComponent.delete()];
    if (this.isDelegateManagementEnabled) {
      deleteReqs.push(this.unansweredCallForwardingSettingsComponent.delete());
    }
    return forkJoin(deleteReqs).pipe(
      delayWhen(() => this._microsoft360ViewContext.refresh(this.upn)),
      map(() => true),
    );
  }
}
